// src/components/sat/TextZoom.tsx
'use client'

export const ZOOM_LEVELS = [1, 1.25, 1.5] as const
export type ZoomLevel = (typeof ZOOM_LEVELS)[number]

/**
 * In-app text-size / zoom control for the runner toolbar (#784). Controlled by
 * the runner (which applies the level as CSS `zoom` to the question content and
 * persists it for the session). Steps between fixed levels with −/+ buttons,
 * disabling at the bounds, and announces the current percentage.
 */
export default function TextZoom({
  level,
  onChange,
  largePrint = false,
}: {
  level: number
  onChange: (level: number) => void
  /** Show a persistent "Large print" badge when the accommodation is on (#49). */
  largePrint?: boolean
}) {
  const idx = Math.max(0, (ZOOM_LEVELS as readonly number[]).indexOf(level))
  const atMin = idx <= 0
  const atMax = idx >= ZOOM_LEVELS.length - 1
  const pct = Math.round(level * 100)

  const btn =
    'inline-flex h-9 min-w-[36px] items-center justify-center rounded-md border border-slate-300 bg-white px-2 text-sm font-medium text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-white'

  return (
    <div
      className="flex items-center gap-1"
      role="group"
      aria-label={largePrint ? 'Text size (large print accommodation enabled)' : 'Text size'}
    >
      {largePrint && (
        <span
          className="rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-ink"
          aria-label="Large print accommodation enabled"
          title="Large print accommodation enabled"
        >
          Large print
        </span>
      )}
      <button
        type="button"
        className={btn}
        disabled={atMin}
        onClick={() => onChange(ZOOM_LEVELS[Math.max(0, idx - 1)])}
        aria-label="Decrease text size"
      >
        A−
      </button>
      <span
        className="min-w-[40px] text-center text-xs tabular-nums text-slate-700"
        aria-live="polite"
      >
        {pct}%
      </span>
      <button
        type="button"
        className={btn}
        disabled={atMax}
        onClick={() => onChange(ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, idx + 1)])}
        aria-label="Increase text size"
      >
        A+
      </button>
    </div>
  )
}
